import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { compose, filter, includes, isNil, map, sort } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';
import { useDispatch } from 'react-redux';

import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider } from 'components';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { customSearchMarketPlaceLeads, onTaskTypeChange, resetFormCriteria } from 'features/views/marketPlace/actions';
import { FIELDS } from 'features/views/marketPlace/form';
import { sortAlphabeticallyBy } from 'utils';

const MarketPlaceForm = ({ taskTypes, taskSubTypes }) => {
	const dispatch = useDispatch();
	const taskType = useFormFieldValue({ fieldName: FIELDS.taskType });

	const taskTypesOptions = useMemo(() => {
		return compose(
			sort(sortAlphabeticallyBy('description')),
			filter((taskType) => taskType?.id >= 100), // leadsOnly
			map((taskType) => ({ ...taskType, id: Number(taskType?.id) })),
		)(taskTypes);
	}, [taskTypes]);

	const splitTaskTypeIds = ({ taskTypeIds, ...rest }) => ({
		...rest,
		taskTypeIds: taskTypeIds.split(','),
	});

	const taskSubtypesOptions = useMemo(() => {
		const formattedTaskTypeId = !isNilOrEmpty(taskType) && taskType.toString();

		return compose(
			sort(sortAlphabeticallyBy('description')),
			filter(({ taskTypeIds }) => includes(formattedTaskTypeId)(taskTypeIds)),
			map(splitTaskTypeIds),
			filter(({ taskTypeIds }) => !isNil(taskTypeIds)),
		)(taskSubTypes);
	}, [taskSubTypes, taskType]);

	const isTaskSubtypeDisabled = isNil(taskSubtypesOptions)
		? true
		: isNilOrEmptyString(taskType) || taskSubtypesOptions.length === 1;

	const handleResetFormCriteria = useCallback(() => {
		dispatch(resetFormCriteria());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(customSearchMarketPlaceLeads());
	}, [dispatch]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleSubmit });

	const handleChangeTypeSelect = useCallback(
		(taskSubtypesOptions) => () => {
			dispatch(onTaskTypeChange(taskSubtypesOptions));
		},
		[dispatch],
	);

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.taskType}
							label={getLabelMessage('marketPlace:taskType')}
							options={taskTypesOptions}
							labelPropName="description"
							onChange={handleChangeTypeSelect(taskSubtypesOptions)}
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.taskSubTypeId}
							label={getLabelMessage('marketPlace:taskSubTypeId')}
							options={taskSubtypesOptions}
							disabled={isTaskSubtypeDisabled}
							labelPropName="description"
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('marketPlace:firstName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('marketPlace:surname')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.businessName}
							label={getLabelMessage('marketPlace:businessName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.BUSINESS_NAME,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.businessIn}
								label={getLabelMessage('marketPlace:businessIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.TAX_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:birthNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.personIn}
								label={getLabelMessage('marketPlace:personIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.BIRTH_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.municipality}
							label={getLabelMessage('marketPlace:municipality')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.MUNICIPALITY,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:postalCode')}>
						<Grid size={1}>
							<TextField
								size="small"
								fieldName={FIELDS.zipCode}
								label={getLabelMessage('marketPlace:zipCode')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.ZIP_CODE,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:policyNo')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.policyNumber}
								label={getLabelMessage('marketPlace:policyNo')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.POLICY_NO,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={8} />
					<Grid size={2}>
						<Button variant="outlined" onClick={handleResetFormCriteria} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={2}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							onClickError={handleSubmitWrongPolicyNoCDV}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

MarketPlaceForm.propTypes = {
	taskSubTypes: PropTypes.array,
	taskTypes: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.taskTypes, 'taskTypes'),
	codelistProvider(codelists.taskSubtypes, 'taskSubTypes'),
)(MarketPlaceForm);
